import { useTranslation } from "@sagot/i18n";
import { Checkbox, ChoiceChips, Skeleton, Slider } from "@sagot/ui";
import { useSources } from "../shared/queries";
import type { GenerateIn } from "../shared/types";

export const GENERATE_MIN = 10;
export const GENERATE_MAX = 100;
const GENERATE_STEP = 5;
const SOURCES_SHOWN = 12;
type Option = "taglish" | "refusals" | "multi_step";
const OPTIONS: readonly Option[] = ["taglish", "refusals", "multi_step"];

export interface GenerateValue {
  source_ids: string[];
  count: number;
  taglish: boolean;
  refusals: boolean;
  multi_step: boolean;
}

export const DEFAULT_GENERATE: GenerateValue = { source_ids: [], count: 30, taglish: true, refusals: true, multi_step: false };

export function toGenerateIn(value: GenerateValue): GenerateIn {
  return { ...value };
}

/** Sources checklist, question count and option chips for AI case generation. */
export function GenerateOptions({ value, onChange, disabled }: { value: GenerateValue; onChange: (value: GenerateValue) => void; disabled?: boolean }) {
  const { t } = useTranslation();
  const sources = useSources();
  const ready = (sources.data ?? []).filter((s) => s.status === "ready").slice(0, SOURCES_SHOWN);
  const labels: Record<Option, string> = { taglish: t("build.evaluations.generate.taglish"), refusals: t("build.evaluations.generate.refusals"), multi_step: t("build.evaluations.generate.multiStep") };
  return (
    <div className="space-y-4">
      <fieldset>
        <legend className="label">{t("build.evaluations.generate.documents")}</legend>
        {sources.isPending ? <Skeleton className="h-16" /> : ready.length === 0 ? <p className="text-xs text-fg-muted">{t("build.evaluations.generate.noDocuments")}</p> : (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {ready.map((source) => (
              <div key={source.id} className="rounded-md border border-line px-3 py-2">
                <Checkbox
                  label={source.title}
                  disabled={disabled}
                  checked={value.source_ids.includes(source.id)}
                  onCheckedChange={(on) => { onChange({ ...value, source_ids: on === true ? [...value.source_ids, source.id] : value.source_ids.filter((id) => id !== source.id) }); }}
                />
              </div>
            ))}
          </div>
        )}
      </fieldset>
      <Slider label={t("build.evaluations.generate.count")} value={value.count} onValueChange={(count) => { onChange({ ...value, count }); }} min={GENERATE_MIN} max={GENERATE_MAX} step={GENERATE_STEP} disabled={disabled} />
      <ChoiceChips
        multiple
        aria-label={t("build.evaluations.generate.options")}
        options={OPTIONS.map((o) => ({ value: o, label: labels[o], disabled }))}
        value={OPTIONS.filter((o) => value[o])}
        onValueChange={(next: string[]) => { onChange({ ...value, taglish: next.includes("taglish"), refusals: next.includes("refusals"), multi_step: next.includes("multi_step") }); }}
      />
    </div>
  );
}
